import React from "react";
import {
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import produce from "immer";
import { initialState } from "./initialState";

export const fieldOfVisionHeaders = ["Field Of Vision", "R-Eye", "L-Eye", "Both", "Results"];

interface FieldOfVisionProps {
  visionData: typeof initialState;
  onVisionChange: (updatedVision: typeof initialState) => void;
}

const ROWS = ["Horizontal Plane", "Vertial Plane"] as const;
const COLS = [
  { key: "rEye", label: "R-Eye" },
  { key: "lEye", label: "L-Eye" },
  { key: "both", label: "Both" },
  { key: "results", label: "Results" }
] as const;

export default function FieldOfVision({
  visionData,
  onVisionChange
}: FieldOfVisionProps): React.JSX.Element {
  const handleChange = (
    row: (typeof ROWS)[number],
    col: (typeof COLS)[number]["key"],
    value: string
  ) => {
    const updated = produce(visionData, (draft: typeof initialState) => {
      if (!draft.vision) {
        draft.vision = JSON.parse(JSON.stringify(initialState.vision));
      }
      const { fieldOfVision } = draft.vision;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const eyeData = fieldOfVision?.[row] as unknown as Record<string, any>;
      if (eyeData) {
        eyeData[col] = value === "" ? null : value;
      }
    });
    onVisionChange(updated);
  };

  const currentFieldOfVision = visionData?.vision?.fieldOfVision || {};

  const cellStyle = {
    border: 1,
    borderColor: "divider"
  };

  const headerCellStyle = {
    ...cellStyle,
    fontWeight: 600,
    textAlign: "center" as const
  };

  return (
    <TableContainer>
      <Typography
        sx={{
          padding: 1,
          textAlign: "center",
          backgroundColor: "#ededed",
          borderLeft: 1,
          borderRight: 1,
          borderTop: 1,
          borderColor: "divider"
        }}
      >
        Field of Vision
      </Typography>

      <Table size="small" sx={{ border: 1, borderColor: "divider", borderRadius: "5px" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerCellStyle, width: "150px" }} />
            {COLS.map((col) => (
              <TableCell key={col.key} sx={headerCellStyle}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {ROWS.map((row) => (
            <TableRow key={row}>
              <TableCell sx={{ ...headerCellStyle, textAlign: "left" }}>{row}</TableCell>
              {COLS.map((col) => (
                <TableCell key={col.key} sx={cellStyle}>
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={currentFieldOfVision[row]?.[col.key] ?? ""}
                    onChange={(e) => handleChange(row, col.key, e.target.value)}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "4px 8px",
                        fontSize: "0.875rem"
                      }
                    }}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
